import { CheckIcon, CaretDownIcon, CaretUpIcon } from "@radix-ui/react-icons"
import * as S from "@radix-ui/react-select"
import { cn, tv, VariantProps } from "@utils/tw"
import { forwardRef, Fragment, ReactNode } from "react"
import { Icon, IconRef } from "./Icon"
import { inputStyles } from "./Input"
import { dropdownItemStyles, dropdownMenuStyles } from "./menu-helpers"
import { Spinner } from "./Spinner"

type Select = VariantProps<typeof selectStyles> & {
  icon?: IconRef
  value?: string
  className?: string
  placeholder?: string
  onValueChange?: (value: string | undefined) => void
  items: (SelectItem | SelectGroup)[]
  required?: boolean
  side?: "top" | "right" | "bottom" | "left"
  align?: "start" | "center" | "end"
  triggerLabelClassName?: string
}

type SelectItem = {
  type?: "item"
  label: string | ReactNode
  value: string
}

type SelectGroup = {
  type: "group"
  label: string | ReactNode
  items: SelectItem[]
}

export const Select = forwardRef<HTMLInputElement, Select>(
  ({ className, triggerLabelClassName, stretch, icon, loading, variant, size, side = "bottom", ...props }, ref) => {
    const styles = selectStyles({
      variant,
      size,
      loading,
      stretch,
    })
    const dropdownStyles = dropdownMenuStyles()
    const headingStyles = dropdownItemStyles({
      caption: true,
      selectable: false,
    })
    return (
      <S.Root value={props.value} onValueChange={props.onValueChange} required={props.required}>
        <S.Trigger className={cn(styles.base(), className)}>
          {/* {icon ? <Icon data-part="icon" name={icon} /> : null} */}
          <span className={cn(styles.triggerLabel(), triggerLabelClassName)}>
            <S.Value placeholder={props.placeholder ?? "Select..."} />
          </span>
          <S.Icon>
            <Icon name="chevronDown" />
          </S.Icon>
          {/* {loading ? <Spinner data-part="spinner" /> : null} */}
        </S.Trigger>
        <S.Portal>
          <S.Content
            position="popper"
            className={cn(
              dropdownStyles.content(),
              "min-w-[--radix-select-trigger-width] max-h-[--radix-select-content-available-height]",
            )}
            side={side}
            align={props.align}
            sideOffset={3}
            avoidCollisions
          >
            <S.ScrollUpButton className={scrollButton()} data-disabled>
              <CaretUpIcon />
            </S.ScrollUpButton>
            <S.Viewport className="p-2">
              {props.items.map((item, i) => (
                <Fragment key={i}>
                  {item.type === "group" ? (
                    <S.Group className={dropdownStyles.items()}>
                      <S.Label className={headingStyles.base()}>
                        <span className={headingStyles.indicator()}></span>
                        <span className={headingStyles.label()}>{item.label}</span>
                      </S.Label>
                      {item.items.map((item, i) => {
                        return <SelectItem {...item} />
                      })}
                    </S.Group>
                  ) : (
                    <SelectItem {...item} />
                  )}
                </Fragment>
              ))}
            </S.Viewport>
            <S.ScrollDownButton className={scrollButton()}>
              <CaretDownIcon />
            </S.ScrollDownButton>
          </S.Content>
        </S.Portal>
      </S.Root>
    )
  },
)
Select.displayName = "Select"

function SelectItem(props: SelectItem) {
  const itemStyles = dropdownItemStyles()
  return (
    <S.Item
      value={props.value}
      className={itemStyles.base()}
      textValue={typeof props.label === "string" ? props.label : undefined}
    >
      <span className={itemStyles.indicator()}>
        <S.ItemIndicator>
          <CheckIcon />
        </S.ItemIndicator>
      </span>
      <S.ItemText className={itemStyles.label()}>{props.label}</S.ItemText>
    </S.Item>
  )
}

const selectStyles = tv({
  extend: inputStyles,
  base: "outline-none",
  slots: {
    triggerLabel: "type-label-m flex-auto text-left mt-[0.3em]",
  },
  variants: {
    stretch: {
      true: "w-full",
    },
  },
})

const scrollButton = tv({
  base: "flex cursor-default items-center justify-center py-1",
})
